import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useProjectContext } from '../contexts/ProjectContext';
import { calcolaResoconto, resocontoToCsv } from '../services/resocontoCalculations';
import { formatCurrency } from '../services/cashFlowCalculations';
import type { EstimateLevel } from '../types';

export function Resoconto() {
  const { currentProject } = useProjectContext();
  const [estimateLevel, setEstimateLevel] = useState<EstimateLevel>('med');

  const resoconto = useMemo(() => {
    if (!currentProject) return null;
    return calcolaResoconto({
      preventivi: currentProject.preventivi ?? [],
      costiExtra: currentProject.costiExtra ?? [],
      finanziamenti: currentProject.finanziamenti ?? [],
      lavori: currentProject.lavori ?? [],
      immobile: currentProject.immobile ?? null,
      estimateLevel,
    });
  }, [currentProject, estimateLevel]);

  if (!currentProject) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Seleziona un progetto per visualizzare il resoconto</p>
      </div>
    );
  }

  if (!resoconto || resoconto.righe.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Resoconto Spese</h2>
          <p className="text-gray-600 mt-1">Speso e da spendere, per categoria</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <div className="text-5xl mb-3">🧾</div>
          <h3 className="text-lg font-medium text-gray-900">Nessuna spesa registrata</h3>
          <p className="mt-2 text-sm text-gray-500">
            Aggiungi voci al computo o registra un costo sostenuto per vedere qui il resoconto.
          </p>
          <Link
            to="/preventivi"
            className="inline-block mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
          >
            Vai ai Preventivi
          </Link>
        </div>
      </div>
    );
  }

  const { righe, totali, prezzoImmobile, copertura } = resoconto;
  const totaleProgetto = prezzoImmobile + totali.speso + totali.daSpendere;

  function scaricaCsv() {
    if (!resoconto) return;
    const blob = new Blob([resocontoToCsv(resoconto)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `resoconto-spese-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Resoconto Spese</h2>
          <p className="text-gray-600 mt-1">Speso e da spendere, per categoria</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Stima:</span>
            <div className="flex bg-gray-100 rounded-lg p-1">
              {(['min', 'med', 'max'] as const).map((level) => (
                <button
                  key={level}
                  onClick={() => setEstimateLevel(level)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    estimateLevel === level
                      ? 'bg-white shadow text-primary-700'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {level === 'min' ? 'Minima' : level === 'med' ? 'Media' : 'Massima'}
                </button>
              ))}
            </div>
          </div>
          <button onClick={scaricaCsv} className="btn-secondary whitespace-nowrap">
            Esporta CSV
          </button>
        </div>
      </div>

      {/* Cards riepilogo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-500">Già speso</p>
          <p className="text-2xl font-bold text-red-600 mt-1" data-testid="totale-speso">
            {formatCurrency(totali.speso)}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            esclusa la casa
            {totali.attrezzi > 0 && `, di cui ${formatCurrency(totali.attrezzi)} in attrezzi`}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-500">Ancora da spendere</p>
          <p className="text-2xl font-bold text-amber-600 mt-1" data-testid="totale-previsto">
            {formatCurrency(totali.daSpendere)}
          </p>
          <p className="text-xs text-gray-400 mt-1">stima {estimateLevel}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-500">Totale progetto</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {formatCurrency(totaleProgetto)}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            inclusa la casa ({formatCurrency(prezzoImmobile)})
          </p>
        </div>

        <div
          className={`rounded-lg shadow p-6 ${
            copertura.saldo >= 0 ? 'bg-white' : 'bg-orange-50 border border-orange-200'
          }`}
        >
          <p className="text-sm text-gray-500">
            {copertura.saldo >= 0 ? 'Margine residuo' : 'Scoperto'}
          </p>
          <p
            className={`text-2xl font-bold mt-1 ${
              copertura.saldo >= 0 ? 'text-green-600' : 'text-orange-600'
            }`}
            data-testid="saldo-copertura"
          >
            {formatCurrency(Math.abs(copertura.saldo))}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            copertura {formatCurrency(copertura.totale)}
          </p>
        </div>
      </div>

      {/* Tabella */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Dettaglio per voce</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-6 py-3 text-left font-medium">Voce</th>
                <th className="px-6 py-3 text-right font-medium">Stima totale</th>
                <th className="px-6 py-3 text-right font-medium">Speso</th>
                <th className="px-6 py-3 text-right font-medium">Da spendere</th>
                <th className="px-6 py-3 text-right font-medium">Scostamento</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(['acquisto', 'lavori', 'attrezzi'] as const).map((gruppo) => {
                const delGruppo = righe.filter((r) => r.gruppo === gruppo);
                if (delGruppo.length === 0) return null;

                return [
                  <tr key={`h-${gruppo}`} className="bg-gray-50/70">
                    <td
                      colSpan={5}
                      className="px-6 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      {gruppo === 'acquisto'
                        ? 'Acquisto e oneri'
                        : gruppo === 'lavori'
                          ? 'Lavori'
                          : 'Attrezzatura (resta a fine cantiere)'}
                    </td>
                  </tr>,
                  ...delGruppo.map((r) => (
                    <tr key={r.chiave} className="hover:bg-gray-50">
                      <td className="px-6 py-3">
                        <p className="font-medium text-gray-900">{r.etichetta}</p>
                        {r.gruppo === 'lavori' && (
                          <p className="text-xs text-gray-400">
                            {r.vociPagate}/{r.vociTotali} voci pagate
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-3 text-right text-gray-700 currency">
                        {formatCurrency(r.stimaTotale)}
                      </td>
                      <td className="px-6 py-3 text-right text-gray-900 font-medium currency">
                        {r.speso > 0 ? formatCurrency(r.speso) : '—'}
                      </td>
                      <td className="px-6 py-3 text-right text-gray-700 currency">
                        {r.daSpendere > 0 ? formatCurrency(r.daSpendere) : '—'}
                      </td>
                      <td className="px-6 py-3 text-right currency">
                        {r.scostamento === null ? (
                          <span className="text-gray-300">—</span>
                        ) : (
                          <span
                            className={r.scostamento > 0 ? 'text-red-600' : 'text-green-600'}
                          >
                            {r.scostamento > 0 ? '+' : ''}
                            {formatCurrency(r.scostamento)}
                            {r.scostamentoPerc !== null && (
                              <span className="text-xs text-gray-400 ml-1">
                                ({r.scostamentoPerc > 0 ? '+' : ''}
                                {r.scostamentoPerc.toFixed(1)}%)
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                    </tr>
                  )),
                ];
              })}
            </tbody>
            <tfoot className="bg-gray-50 font-semibold text-gray-900">
              <tr>
                <td className="px-6 py-3">Totale</td>
                <td className="px-6 py-3 text-right currency">
                  {formatCurrency(totali.stimaTotale)}
                </td>
                <td className="px-6 py-3 text-right currency">{formatCurrency(totali.speso)}</td>
                <td className="px-6 py-3 text-right currency">
                  {formatCurrency(totali.daSpendere)}
                </td>
                <td className="px-6 py-3 text-right currency">
                  {totali.scostamento === null ? '—' : formatCurrency(totali.scostamento)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Copertura */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Copertura finanziaria</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Mutui</p>
            <p className="font-semibold text-gray-900 currency">
              {formatCurrency(copertura.mutui)}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Prestiti</p>
            <p className="font-semibold text-gray-900 currency">
              {formatCurrency(copertura.prestiti)}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Fondi propri</p>
            <p className="font-semibold text-gray-900 currency">
              {formatCurrency(copertura.fondiPropri)}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Totale</p>
            <p className="font-semibold text-primary-700 currency">
              {formatCurrency(copertura.totale)}
            </p>
          </div>
        </div>

        {copertura.saldo < 0 && (
          <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-4">
            <p className="text-sm text-orange-800">
              Gli impieghi previsti superano la copertura di{' '}
              <strong>{formatCurrency(Math.abs(copertura.saldo))}</strong>. Rivedi le voci a
              computo, oppure valuta come finanziare la differenza.
            </p>
          </div>
        )}
      </div>

      {/* Nota metodologica */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Come si legge</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>Speso</strong>: costi sostenuti registrati e voci a computo con consuntivo</li>
          <li>• <strong>Da spendere</strong>: stima delle voci ancora senza consuntivo</li>
          <li>
            • <strong>Scostamento</strong>: consuntivo meno stima, calcolato solo sulle voci già
            pagate
          </li>
          <li>• Le voci disattivate nel computo sono escluse da tutti i totali</li>
          <li>• Il prezzo della casa è escluso dai totali di spesa: lo copre il mutuo</li>
          <li>
            • L'<strong>attrezzatura</strong> (categoria costo «attrezzi») è contata nello speso ma
            tenuta a parte: non è opera e resta tua a fine cantiere
          </li>
        </ul>
      </div>
    </div>
  );
}
